import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge } from './design';

const CORE_API = '/api';

const logSourcesDef = [
  { id: 'syslog', label: 'Syslog', group: 'system' },
  { id: 'auth', label: 'Auth', group: 'system' },
  { id: 'kern', label: 'Kernel', group: 'system' },
  { id: 'dmesg', label: 'Dmesg', group: 'system' },
  { id: 'app', label: 'App Events', group: 'events' },
  { id: 'db', label: 'DB Events', group: 'events' },
  { id: 'web', label: 'Web Events', group: 'events' },
  { id: 'nginx', label: 'Nginx', group: 'events' },
  { id: 'mysql', label: 'MySQL', group: 'events' },
  { id: 'postgres', label: 'PostgreSQL', group: 'events' },
  { id: 'docker', label: 'Docker', group: 'events' },
  { id: 'exporter', label: 'Exporters', group: 'events' },
  { id: 'wazuh', label: 'Wazuh Alerts', group: 'wazuh' },
  { id: 'redis_log', label: 'Redis', group: 'agents', unit: 'redis' },
  { id: 'node_exporter_log', label: 'Node Exporter', group: 'agents', unit: 'node_exporter' },
  { id: 'wazuh_agent_log', label: 'Wazuh Agent', group: 'agents', unit: 'wazuh-agent' },
  { id: 'nginx_log', label: 'Nginx (svc)', group: 'agents', unit: 'nginx' },
  { id: 'mysql_log', label: 'MySQL (svc)', group: 'agents', unit: 'mysql' },
  { id: 'pgsql_log', label: 'PostgreSQL', group: 'agents', unit: 'postgresql' },
  { id: 'docker_log', label: 'Docker (svc)', group: 'agents', unit: 'docker' },
  { id: 'gitlab_log', label: 'GitLab', group: 'agents', unit: 'gitlab-runsvdir' },
  { id: 'php_fpm_log', label: 'PHP-FPM', group: 'agents', unit: 'php*-fpm' },
  { id: 'httpd_log', label: 'HTTPD', group: 'agents', unit: 'httpd' },
  { id: 'cockpit_log', label: 'Cockpit', group: 'agents', unit: 'cockpit' },
  { id: 'clickhouse_log', label: 'ClickHouse', group: 'agents', unit: 'clickhouse-server' },
  { id: 'direct', label: 'Direct (SSH)', group: 'live' },
];

const severityColors = {
  critical: 'red',
  error: 'red',
  warn: 'amber',
  info: 'blue',
  debug: 'gray',
};

const journaldPriorityMap = { '0': 'emerg', '1': 'alert', '2': 'crit', '3': 'err', '4': 'warning', '5': 'notice', '6': 'info', '7': 'debug' };

const selectStyle = { fontSize: 12, border: '1px solid var(--border-strong)', borderRadius: 8, padding: '4px 8px', background: 'var(--surface)', color: 'var(--text-2)', outline: 'none' };

export default function ServerLogs({ server }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logSource, setLogSource] = useState('syslog');
  const [severity, setSeverity] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [viewMode, setViewMode] = useState('detailed');
  const [directUnit, setDirectUnit] = useState('');
  const [availableUnits, setAvailableUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const sev = (log) => logSource === 'direct' && log.priority ? (journaldPriorityMap[log.priority] || 'info') : (log.severity || 'info');

  const fetchLogs = useCallback(async (loadMore = false) => {
    setLoading(true);
    setError(null);
    try {
      if (!server?.id) throw new Error('Server not loaded');
      const isSystemLog = ['syslog', 'auth', 'kern', 'dmesg'].includes(logSource);
      const agentLog = logSourcesDef.find(s => s.id === logSource);
      const isAgentLog = agentLog?.group === 'agents';

      if (logSource === 'direct' || isSystemLog || isAgentLog) {
        const params = new URLSearchParams({ lines: String(limit) });
        if (isAgentLog && agentLog.unit) params.set('unit', agentLog.unit);
        if (logSource === 'direct' && directUnit) params.set('unit', directUnit);
        if (logSource === 'kern' || logSource === 'dmesg') params.set('source', 'kern');
        const res = await fetch(`${CORE_API}/logs/direct/${server.id}?${params}`);
        if (!res.ok) throw new Error(`Direct SSH: HTTP ${res.status}`);
        const data = await res.json();
        setLogs(loadMore ? [...logs, ...(data.logs || [])] : (data.logs || []));
      } else {
        const params = new URLSearchParams({ host: server.ip_address, type: logSource, limit: String(limit) });
        if (severity) params.set('severity', severity);
        const res = await fetch(`${CORE_API}/logs?${params}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const chLogs = data.logs || [];
        if (chLogs.length > 0) {
          setLogs(loadMore ? [...logs, ...chLogs] : chLogs);
        } else {
          const directParams = new URLSearchParams({ lines: String(limit) });
          const directRes = await fetch(`${CORE_API}/logs/direct/${server.id}?${directParams}`);
          if (directRes.ok) {
            const directData = await directRes.json();
            setLogs(directData.logs || []);
          } else {
            setLogs([]);
          }
        }
      }
    } catch (e) {
      setError(e.message);
      if (!loadMore) setLogs([]);
    }
    setLoading(false);
  }, [server, logSource, severity, limit, directUnit]);

  const fetchUnits = useCallback(async () => {
    if (!server?.id) return;
    setLoadingUnits(true);
    try {
      const res = await fetch(`${CORE_API}/servers/${server.id}/systemd-units`);
      if (res.ok) {
        const data = await res.json();
        setAvailableUnits(data.units || []);
      }
    } catch {
      // ignore
    }
    setLoadingUnits(false);
  }, [server]);

  useEffect(() => {
    setLogs([]);
    if (logSource === 'direct') {
      fetchUnits();
    }
    fetchLogs();
  }, [logSource, severity, limit, server.ip_address, directUnit, fetchLogs, fetchUnits]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchLogs(), 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  const filteredLogs = search
    ? logs.filter(l => JSON.stringify(l).toLowerCase().includes(search.toLowerCase()))
    : logs;

  const copyLog = (log) => {
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
  };

  const copyAll = () => {
    navigator.clipboard.writeText(JSON.stringify(filteredLogs, null, 2));
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      return formatTime(ts);
    } catch { return ts; }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          {[{ label: 'System', sources: logSourcesDef.filter(s => s.group === 'system') },
            { label: 'Events', sources: logSourcesDef.filter(s => s.group === 'events') },
            { label: 'Agents', sources: logSourcesDef.filter(s => s.group === 'agents') },
            { label: 'Security', sources: logSourcesDef.filter(s => s.group === 'wazuh') },
            { label: 'Live', sources: logSourcesDef.filter(s => s.group === 'live') },
          ].map(group => (
            <div key={group.label} className="flex items-center" style={{ gap: 4, marginRight: 4 }}>
              <span className="muted" style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginRight: 2 }}>{group.label}:</span>
              {group.sources.map(s => (
                <button key={s.id} onClick={() => {
                  setLogSource(s.id);
                  if (s.unit) setDirectUnit(s.unit);
                }}
                  className={`chip ${logSource === s.id ? 'active' : ''}`}>
                  {s.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <select value={severity} onChange={e => setSeverity(e.target.value)}>
          <option value="">All levels</option>
          <option value="critical">Critical</option>
          <option value="error">Error</option>
          <option value="warn">Warning</option>
          <option value="info">Info</option>
          <option value="debug">Debug</option>
        </select>
        <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
          <option value={500}>500</option>
          <option value={1000}>1000</option>
        </select>
        {logSource === 'direct' && (
          <div className="flex items-center" style={{ gap: 4 }}>
            <select value={directUnit} onChange={e => { setDirectUnit(e.target.value); }} style={{ ...selectStyle, maxWidth: 160 }}>
              <option value="">All units</option>
              {availableUnits.map(u => (
                <option key={u.name} value={u.name.replace('.service', '')}>{u.name.replace('.service', '')}</option>
              ))}
            </select>
            <button onClick={fetchUnits} disabled={loadingUnits} className="chip">
              {loadingUnits ? '...' : 'Scan'}
            </button>
          </div>
        )}
        <button onClick={() => { setViewMode(viewMode === 'compact' ? 'detailed' : 'compact'); }} className="chip">
          {viewMode === 'compact' ? 'Detailed' : 'Compact'}
        </button>
        <button onClick={() => setAutoRefresh(!autoRefresh)} className={`chip ${autoRefresh ? 'active' : ''}`}>
          {autoRefresh ? 'Live' : 'Auto'}
        </button>
        <Button size="sm" onClick={() => fetchLogs()} disabled={loading}>
          {loading ? '...' : 'Refresh'}
        </Button>
      </div>

      {/* Search */}
      <div className="filter-bar" style={{ marginBottom: 12, position: 'relative' }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search logs... (client-side filter)"
          style={{ flex: 1, paddingLeft: 32 }} />
        <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {filteredLogs.length > 0 && (
          <span className="muted" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 10 }}>
            {filteredLogs.length} / {logs.length}
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 12, padding: 12, borderRadius: 10, background: 'rgba(248,113,113,0.10)', border: '1px solid var(--danger)', fontSize: 12, color: 'var(--danger)' }}>
          Failed to load logs: {error}
        </div>
      )}

      {/* Logs */}
      {loading && logs.length === 0 ? (
        <div className="empty">Loading logs...</div>
      ) : filteredLogs.length === 0 ? (
        <div className="empty">
          {search ? 'No logs match your search' : (logSource === 'direct' ? 'Could not fetch logs via SSH. Check server connectivity.' : 'No logs found in database for this server')}
        </div>
      ) : (
        <>
          <div className="flex between center" style={{ marginBottom: 8 }}>
            <span className="muted" style={{ fontSize: 12 }}>
              Showing {filteredLogs.length} of {logs.length} logs
            </span>
            <button onClick={copyAll}
              style={{ fontSize: 12, color: 'var(--accent-1)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
              Copy all
            </button>
          </div>
          <div className="mono" style={{ background: '#0b1220', borderRadius: 12, fontSize: 12, maxHeight: '520px', overflow: 'auto', border: '1px solid var(--glass-border)' }}>
            {filteredLogs.map((log, i) => (
              <div key={i}>
                <div
                  onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                  className="flex"
                  style={{ alignItems: 'flex-start', gap: 8, padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-2)' }}
                >
                  {/* Severity dot */}
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: sev(log) === 'crit' || sev(log) === 'critical' || sev(log) === 'emerg' ? 'var(--danger)' : sev(log) === 'err' || sev(log) === 'error' ? '#f87171' : sev(log) === 'warn' || sev(log) === 'warning' ? 'var(--warning)' : sev(log) === 'info' || sev(log) === 'notice' ? 'var(--info)' : 'var(--text-3)' }} />

                  {/* Compact: just message */}
                  {viewMode === 'compact' ? (
                    <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {log.message || log.raw || String(log)}
                    </span>
                  ) : (
                    <>
                      {/* Timestamp */}
                      <span style={{ color: 'var(--text-3)', flexShrink: 0, width: 144, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatTime(log.timestamp)}>
                        {formatTime(log.timestamp)}
                      </span>
                      {/* Service / Unit */}
                      {(log.service || log.unit) && (
                        <span style={{ color: 'var(--accent-3)', flexShrink: 0, width: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.service || log.unit}</span>
                      )}
                      {/* Severity badge */}
                      {sev(log) && logSource !== 'wazuh' && (
                        <Badge tone={severityColors[sev(log)] || 'gray'} dot={false}>{sev(log)}</Badge>
                      )}
                      {/* Rule level for wazuh */}
                      {logSource === 'wazuh' && log.rule_level && (
                        <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 500, flexShrink: 0, background: Number(log.rule_level) >= 12 ? 'rgba(248,113,113,0.15)' : Number(log.rule_level) >= 8 ? 'rgba(251,146,60,0.15)' : Number(log.rule_level) >= 5 ? 'rgba(251,191,36,0.15)' : 'var(--glass)', color: Number(log.rule_level) >= 12 ? 'var(--danger)' : Number(log.rule_level) >= 8 ? '#fb923c' : Number(log.rule_level) >= 5 ? 'var(--warning)' : 'var(--text-2)' }}>
                          level {log.rule_level}
                        </span>
                      )}
                      {/* Category */}
                      {log.category && (
                        <span style={{ color: 'var(--accent-2)', flexShrink: 0, width: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.category}</span>
                      )}
                      {/* Message */}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: sev(log) === 'critical' || sev(log) === 'emerg' || sev(log) === 'error' || sev(log) === 'err' ? 'var(--danger)' : sev(log) === 'warn' || sev(log) === 'warning' ? 'var(--warning)' : 'var(--text-2)' }}>
                        {log.message || log.raw || String(log)}
                      </span>
                      {/* Copy */}
                      <button onClick={(e) => { e.stopPropagation(); copyLog(log); }}
                        style={{ color: 'var(--text-3)', flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        <svg style={{ width: 12, height: 12 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
                {/* Expanded raw */}
                {expandedIdx === i && log.raw && (
                  <div style={{ padding: '8px 12px', background: '#05060f', borderBottom: '1px solid var(--glass-border)' }}>
                    <pre style={{ fontSize: 10, color: 'var(--text-3)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{log.raw}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Load more */}
          {logs.length >= limit && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Button variant="ghost" size="sm" onClick={() => setLimit(limit + 100)}>Show {100} more</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
